import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import pluralize from 'pluralize';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { ANIMATION_DURATION, Icon, IconButton, Layout, PillButton, StickyContainer, Text } from 'ds';
import { PANTRY_ORDERS_PATH } from 'routes/paths';
import {
  ConsumablesOrder,
  ConsumablesOrderItem,
  ConsumablesProduct,
  DraftConsumablesOrderItem,
  formatDate
} from 'shared';
import { actions } from 'store/Consumables';
import { selectConsumablesOrders, selectDeliveryFees } from 'store/Consumables/selectors';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import MobileOrderSummary from 'ux/Tenant/MobileOrderSummary';

import ConsumablesOrderFeedbackModal from './ConsumablesOrderFeedbackModal';
import OrderProgressBar from './OrderProgressBar';
import ConsumablesAddProductsComboBox from '../ConsumablesAddProductsComboBox';
import ConsumablesProductQuantityCard from '../ConsumablesProductQuantityCard';
import ConsumablesSkipOrderModal from '../ConsumablesSkipOrderModal';
import CostSummaryBox from '../CostSummaryBox';
import MobileSaveAndResetChanges from '../MobileSaveAndResetChanges';
import {
  getConsumablesProductCategories,
  getConsumablesProductDeliveryFees,
  getConsumablesProductTypes,
  getConsumablesProducts,
  updateConsumablesOrderItems
} from '../requests';
import { getConsumablesProductSubscriptions } from '../requests';
import { getConsumablesOrders } from '../requests';
import { buildConsumablesOrderItemsUpdate, calculateDeliveryFee } from '../utils';

const ConsumablesOrderPage = () => {
  const dispatch = useAppDispatch();
  const { isMobile } = useContext(media);
  const { id } = useParams<{ id: string }>();
  const { prevPathname } = useAppSelector(selectUIState);
  const containerRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);
  const [liked, setLiked] = useState<boolean | null>(null);
  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [isSkipOrderModalVisible, setIsSkipOrderModalVisible] = useState(false);
  const consumablesOrders = useAppSelector(selectConsumablesOrders);
  const deliveryFees = useAppSelector(selectDeliveryFees);
  const consumablesOrder = consumablesOrders?.find((order: ConsumablesOrder) => order.id === parseInt(id));
  const lockDate = DateTime.fromISO(consumablesOrder?.delivery_date || '').minus({ days: 8 });
  const [clonedConsumablesOrderItems, setClonedConsumablesOrderItems] = useState<DraftConsumablesOrderItem[]>([
    ...(consumablesOrder?.consumables_order_items || [])
  ]);

  useEffect(() => {
    Promise.all([
      getConsumablesProducts(),
      getConsumablesProductCategories(),
      getConsumablesProductTypes(),
      getConsumablesOrders(),
      getConsumablesProductSubscriptions(),
      getConsumablesProductDeliveryFees()
    ]).then(
      ([
        { data: products },
        { data: newCategories },
        { data: newTypes },
        { data: orders },
        { data: subscriptions },
        { data: deliveryFees }
      ]) => {
        dispatch(actions.setProducts(products));
        dispatch(actions.setCategories(newCategories));
        dispatch(actions.setTypes(newTypes));
        dispatch(actions.setConsumablesOrders(orders));
        const order = orders.find(order => order.id === parseInt(id));
        setClonedConsumablesOrderItems([...(order?.consumables_order_items || [])]);
        dispatch(actions.setConsumablesProductSubscriptions(subscriptions));
        dispatch(actions.setDeliveryFees(deliveryFees));
        dispatch(actions.setConsumablesStateLoaded(true));
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  if (!consumablesOrder) {
    return null;
  }

  const itemsSubtotal = clonedConsumablesOrderItems.reduce(
    (acc, curr) => acc + curr.quantity * curr.consumables_product.amount_cents,
    0
  );

  const deliveryFeeCents = calculateDeliveryFee(clonedConsumablesOrderItems, deliveryFees);

  // Build an object mapping consumables_product.id to order_quantity
  const originalObj =
    consumablesOrder?.consumables_order_items?.reduce(
      (acc, item) => {
        acc[item.consumables_product.id] = item.quantity;
        return acc;
      },
      {} as Record<number, number>
    ) || {};

  const clonedObj = clonedConsumablesOrderItems.reduce(
    (acc, item) => {
      acc[item.consumables_product.id] = item.quantity;
      return acc;
    },
    {} as Record<number, number>
  );

  const changesMade = (() => {
    const originalKeys = Object.keys(originalObj);
    const clonedKeys = Object.keys(clonedObj);

    if (originalKeys.length !== clonedKeys.length) return true;

    for (const key of originalKeys) {
      const numericKey = Number(key);
      if (originalObj[numericKey] !== clonedObj[numericKey]) return true;
    }

    return false;
  })();

  const orderStatus = consumablesOrder.status;
  const itemsRestocked = consumablesOrder.consumables_order_items.reduce(
    (acc: number, item: ConsumablesOrderItem) => acc + item.quantity,
    0
  );
  const prevPathnameSubscription = prevPathname?.endsWith('subscription');
  const prevPathnameShop = prevPathname?.includes('shop');
  const backButtonHref =
    (prevPathnameSubscription || prevPathnameShop) && prevPathname ? prevPathname : PANTRY_ORDERS_PATH;

  const handleSaveChanges = async () => {
    const { orderItems, wouldMutate } = buildConsumablesOrderItemsUpdate(
      consumablesOrder.consumables_order_items || [],
      clonedConsumablesOrderItems
    );

    if (!wouldMutate) {
      setClonedConsumablesOrderItems([...(consumablesOrder?.consumables_order_items || [])]);
      return;
    }

    try {
      const response = await updateConsumablesOrderItems(consumablesOrder.id, orderItems);
      if (response.data.message === 'Cannot update order - a newer order exists.') {
        showSnackbar({
          message: 'Current order is locked. Please see open order for changes.',
          negative: true
        });
        dispatch(actions.setConsumablesProductSubscriptions(response.data.service.consumables_product_subscriptions));
        dispatch(actions.setConsumablesOrders(response.data.service.consumables_orders));
        setClonedConsumablesOrderItems(
          response.data.service.consumables_orders.find((order: ConsumablesOrder) => order.id === parseInt(id))
            ?.consumables_order_items || []
        );
        return;
      } else if (response.status === 200) {
        dispatch(actions.setConsumablesProductSubscriptions(response.data.consumables_product_subscriptions));
        dispatch(actions.setConsumablesOrders(response.data.consumables_orders));
        setClonedConsumablesOrderItems(
          response.data.consumables_orders.find((order: ConsumablesOrder) => order.id === parseInt(id))
            ?.consumables_order_items || []
        );
        showSnackbar({
          message: 'Upcoming order updated'
        });
      }
    } catch (error) {
      showSnackbar({
        message: 'Something went wrong with updating the order items',
        negative: true
      });
    }
  };

  return (
    <Layout flex direction="column" width="100%" height="100%">
      <StickyContainer zIndex={1000}>
        <Layout
          width="100%"
          height={isMobile ? 60 : 88}
          background="white"
          {...(isMobile
            ? { paddingTop: 12, paddingRight: 45, flex: true, align: 'center' }
            : { paddingX: 40, paddingY: 24, borderBottom: true, borderColor: 'gray-50' })}
        >
          <IconButton
            size="lg"
            type="noBackground"
            name={isMobile ? 'leftChevron' : 'leftArrow'}
            href={backButtonHref}
          />
          {isMobile && (
            <Layout flex flexGrow={1} align="center" justify="center">
              <Text size="headline-sm">
                {orderStatus === 'canceled' ? 'Order details' : 'Edit upcoming restocking'}
              </Text>
            </Layout>
          )}
        </Layout>
      </StickyContainer>
      {isMobile && (
        <MobileOrderSummary
          itemsSubtotalCents={itemsSubtotal}
          disclaimerMessage={
            orderStatus === 'open'
              ? `Next charge on ${formatDate({
                  date: lockDate,
                  format: 'MED_WITH_SHORT_WEEKDAY'
                })}`
              : undefined
          }
          showRestockingIncluded={true}
        />
      )}
      <Layout
        {...(isMobile
          ? { paddingX: 24, paddingTop: orderStatus === 'delivered' || orderStatus === 'canceled' ? 32 : 24 }
          : { paddingX: 80, paddingTop: 40 })}
        paddingBottom={64}
        flex
        direction="row"
        gap={80}
        justify="center"
      >
        <Layout
          flex
          direction="column"
          gap={isMobile ? undefined : 36}
          width={724}
          __ref={containerRef}
          onMeasure={() => setTop(containerRef.current?.getBoundingClientRect().top || 0)}
        >
          <Layout
            direction="column"
            gap={12}
            marginBottom={isMobile ? (orderStatus === 'delivered' || orderStatus === 'canceled' ? 36 : 16) : 0}
          >
            {orderStatus === 'open' || orderStatus === 'locked' ? (
              <>
                <Text size={isMobile ? 'headline-xs' : 'headline-lg'}>
                  Upcoming restocking by{' '}
                  {formatDate({ date: consumablesOrder.display_delivery_date, format: 'MED_WITH_SHORT_WEEKDAY' })}
                </Text>
                <Text size={isMobile ? 'body-md' : 'body-lg'} __style={{ letterSpacing: '-0.01em' }}>
                  We'll place the items in your preferred pantry locations.
                </Text>
              </>
            ) : orderStatus === 'canceled' ? (
              <>
                <Text size={isMobile ? 'headline-xs' : 'headline-lg'} color="gray-700">
                  Canceled
                </Text>
                <Text size={isMobile ? 'body-md' : 'body-lg'} color="gray-700">
                  Restocking originally scheduled for{' '}
                  {formatDate({ date: consumablesOrder.display_delivery_date, format: 'MED_WITH_SHORT_WEEKDAY' })} was
                  canceled.
                </Text>
              </>
            ) : (
              <>
                <Layout flex direction="row" gap={8} align="center">
                  {!isMobile && (
                    <Layout
                      width={32}
                      height={32}
                      justify="center"
                      align="center"
                      borderRadius="circular"
                      color="green-700"
                    >
                      <Icon name="checkmark" size="sm" color="white" />
                    </Layout>
                  )}
                  <Text size={isMobile ? 'headline-xs' : 'headline-lg'} color="green-700">
                    Restocking by{' '}
                    {formatDate({ date: consumablesOrder.delivery_date, format: 'MED_WITH_SHORT_WEEKDAY' })}
                  </Text>
                </Layout>
                <Text size={isMobile ? 'body-md' : 'body-lg'}>{itemsRestocked} items were restocked</Text>
              </>
            )}
          </Layout>
          {(orderStatus === 'open' ||
            orderStatus === 'locked' ||
            (orderStatus === 'delivered' && !consumablesOrder.has_given_feedback)) &&
            (orderStatus === 'open' || orderStatus === 'locked' || orderStatus === 'delivered') && (
              <Layout direction="column" gap={12} marginBottom={isMobile ? 32 : 0}>
                {(orderStatus === 'open' || orderStatus === 'locked') && (
                  <>
                    <OrderProgressBar status={orderStatus} />
                    <Text size="body-sm" color="gray-400">
                      {orderStatus === 'open' ? 'Order open' : 'Preparing items'}
                    </Text>
                    {isMobile && orderStatus === 'open' && (
                      <PillButton
                        borderRadius={40}
                        type="secondaryOutline"
                        size="md"
                        text="Skip this order"
                        onClick={() => {
                          setIsSkipOrderModalVisible(true);
                        }}
                      />
                    )}
                  </>
                )}
                {isMobile && orderStatus === 'delivered' && !consumablesOrder.has_given_feedback && (
                  <Layout flex color="gray-25" borderRadius={16} paddingX={24} paddingY={12} gap={24} align="center">
                    <Text size="body-md" semibold>
                      How'd it go?
                    </Text>
                    <Layout flex direction="row" gap={16}>
                      <IconButton
                        size="lg"
                        stroke={2}
                        type="gray"
                        name="thumbsUp"
                        onClick={() => {
                          setIsFeedbackModalVisible(true);
                          setLiked(true);
                        }}
                      />
                      <IconButton
                        size="lg"
                        stroke={2}
                        type="gray"
                        name="thumbsDown"
                        onClick={() => {
                          setIsFeedbackModalVisible(true);
                          setLiked(false);
                        }}
                      />
                    </Layout>
                  </Layout>
                )}
                {isMobile && (
                  <Layout
                    minHeight={8}
                    color="gray-25"
                    position="relative"
                    left={-24}
                    marginTop={18}
                    width="calc(100% + 48px)"
                  />
                )}
                {orderStatus === 'delivered' && !consumablesOrder.has_given_feedback && !isMobile && (
                  <Layout flex color="gray-25" borderRadius={16} paddingX={24} paddingY={12} gap={24} align="center">
                    <Text size="body-md" semibold>
                      How'd it go?
                    </Text>
                    <Layout flex direction="row" gap={16}>
                      <IconButton
                        size="lg"
                        stroke={2}
                        type="gray"
                        name="thumbsUp"
                        onClick={() => {
                          setIsFeedbackModalVisible(true);
                          setLiked(true);
                        }}
                      />
                      <IconButton
                        size="lg"
                        stroke={2}
                        type="gray"
                        name="thumbsDown"
                        onClick={() => {
                          setIsFeedbackModalVisible(true);
                          setLiked(false);
                        }}
                      />
                    </Layout>
                  </Layout>
                )}
              </Layout>
            )}
          <Layout direction="column" gap={12}>
            <Text size={isMobile ? 'body-md' : 'headline-xs'} semibold={isMobile} __style={{ fontWeight: '600' }}>
              {orderStatus === 'open' ? 'Edit items in order' : 'Order summary'}
            </Text>
            {orderStatus === 'open' && (
              <Layout
                color="gold-200"
                borderRadius={8}
                align={isMobile ? 'flex-start' : 'center'}
                paddingX={12}
                paddingY={8}
                flex
                direction="row"
                gap={8}
                marginBottom={isMobile ? 36 : undefined}
              >
                <Icon name="info" size="xs" />
                <Text size="body-sm" color="gray-900" semibold>
                  You can edit or add items to this order through{' '}
                  {formatDate({
                    date: DateTime.fromISO(consumablesOrder.delivery_date).minus({ days: 8 }).toISO(),
                    format: 'MED'
                  })}
                  .
                </Text>
              </Layout>
            )}
          </Layout>
          {orderStatus === 'open' && (
            <ConsumablesAddProductsComboBox
              existingProductSelections={clonedConsumablesOrderItems}
              onSelect={(product: ConsumablesProduct | undefined) => {
                if (product) {
                  if (!clonedConsumablesOrderItems.some(p => p.consumables_product.id === product.id)) {
                    setClonedConsumablesOrderItems(prev => [
                      {
                        consumables_product: product,
                        quantity: 1,
                        consumables_order_id: consumablesOrder.id,
                        locked_amount_cents: null
                      },
                      ...prev
                    ]);
                  }
                }
              }}
            />
          )}
          <Layout marginY={isMobile ? 32 : undefined}>
            <Text size="body-md" semibold>
              {clonedConsumablesOrderItems.length} {pluralize('item', clonedConsumablesOrderItems.length)}
            </Text>
          </Layout>
          <Layout flex direction="column" gap={36}>
            {clonedConsumablesOrderItems.map(productOrderItem => (
              <ConsumablesProductQuantityCard
                showSkipRemoveItemButton={orderStatus === 'open'}
                isSaved={productOrderItem.id !== undefined}
                showQuantitySelector={orderStatus === 'open'}
                removeItem={() => {
                  setClonedConsumablesOrderItems(prev =>
                    prev.filter(p => p.consumables_product.id !== productOrderItem.consumables_product.id)
                  );
                }}
                lockedAmountCents={productOrderItem.locked_amount_cents}
                key={productOrderItem.consumables_product.id}
                product={productOrderItem.consumables_product}
                quantity={productOrderItem.quantity}
                onQuantityChange={quantity => {
                  setClonedConsumablesOrderItems(prev =>
                    prev.map(p =>
                      p.consumables_product.id === productOrderItem.consumables_product.id ? { ...p, quantity } : p
                    )
                  );
                }}
              />
            ))}
            {orderStatus !== 'open' && !isMobile && (
              <Layout borderTop borderColor="gray-50">
                <CostSummaryBox
                  sticky
                  top={top}
                  itemsSubtotalCents={itemsSubtotal}
                  paddingX={0}
                  deliveryFeeCents={deliveryFeeCents}
                  lockedConsumablesOrder={consumablesOrder}
                />
              </Layout>
            )}
          </Layout>
        </Layout>
        {orderStatus === 'open' && !isMobile && (
          <Layout width={320}>
            <CostSummaryBox
              onClickSkipOrder={() => {
                setIsSkipOrderModalVisible(true);
              }}
              showSkipOrderButton={true}
              sticky
              top={top}
              itemsSubtotalCents={itemsSubtotal}
              disclaimerMessage={
                <Text size="body-xs" color="gray-700">
                  Next charge on {formatDate({ date: lockDate, format: 'MED_WITH_SHORT_WEEKDAY' })}
                </Text>
              }
              deliveryFeeCents={deliveryFeeCents}
              changesMade={changesMade}
              onResetChanges={() => {
                setClonedConsumablesOrderItems([...(consumablesOrder?.consumables_order_items || [])]);
              }}
              onClickSaveChanges={handleSaveChanges}
              backgroundColor="gray-25"
            />
          </Layout>
        )}
      </Layout>
      <ConsumablesOrderFeedbackModal
        isVisible={isFeedbackModalVisible}
        onClose={() => {
          setIsFeedbackModalVisible(false);
          setTimeout(() => {
            setLiked(null);
          }, ANIMATION_DURATION);
        }}
        feedbackRequest={{
          task_id: consumablesOrder.task_id as number,
          type: 'restocking',
          date: consumablesOrder.delivery_date ?? '',
          action: null
        }}
        feedback={{
          task_id: consumablesOrder.task_id as number,
          liked: liked ?? false,
          message: null
        }}
        selectedOrder={consumablesOrder}
      />

      {isMobile && changesMade && (
        <MobileSaveAndResetChanges
          onReset={() => {
            setClonedConsumablesOrderItems([...(consumablesOrder?.consumables_order_items || [])]);
          }}
          onSave={handleSaveChanges}
        />
      )}
      <ConsumablesSkipOrderModal
        isVisible={isSkipOrderModalVisible}
        orderId={consumablesOrder.id}
        onClose={() => {
          setIsSkipOrderModalVisible(false);
        }}
        nextOrdersDeliveryDate={consumablesOrder.next_orders_delivery_date}
      />
    </Layout>
  );
};

export default ConsumablesOrderPage;
